import * as DialogPrimitive from "@radix-ui/react-dialog";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { cn } from "@/shared/lib/utils";
import { cva, type VariantProps } from "@/shared/lib/variants";

/**
 * Radix Dialog wrapper for general-purpose modals (forms, picker panels).
 * `AlertDialog` is reserved for destructive confirmations — it forces a
 * confirm/cancel pair and blocks click-outside dismiss, which is the wrong
 * UX for forms.
 *
 * Surface tiers per design.md §8:
 *   overlay → dim, z-overlay
 *   content → glass-surface-strong rounded-2xl, z-modal
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogOverlay = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-overlay",
        "bg-black/30 backdrop-blur-sm",
        className,
      )}
      {...props}
    />
  );
});

/**
 * Each `size` is a SINGLE `max-width` that folds the cap and the universal 8px
 * viewport gutter into one `min()` — so the modal is `min(cap, 100vw - 1rem)`,
 * i.e. "the smaller value wins" (design.md §10). This MUST stay one max-width
 * utility: two competing `max-w-*` classes would be collapsed by `twMerge` to
 * whichever came last, silently dropping the cap (the bug this replaces).
 */
const dialogContent = cva(
  [
    "fixed left-1/2 top-1/2 z-modal w-full -translate-x-1/2 -translate-y-1/2",
    "max-h-[calc(100vh_-_1rem)]",
    "rounded-2xl glass-surface-strong p-6",
    "flex flex-col gap-4",
    "focus:outline-none",
  ],
  {
    variants: {
      size: {
        md: "max-w-[min(28rem,100vw_-_1rem)]",
        lg: "max-w-[min(32rem,100vw_-_1rem)]",
        "2xl": "max-w-[min(42rem,100vw_-_1rem)]",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContent> {}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContent({ className, size, ...props }, ref) {
    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(dialogContent({ size }), className)}
          {...props}
        />
      </DialogPortal>
    );
  },
);

export function DialogHeader({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2">{children}</div>;
}

/**
 * Footer action row. Per design.md §10 it stacks to a full-width column on
 * mobile (primary action on top, DOM order) and lays out as a reversed row at
 * `md`+ (primary action at the trailing/right edge). Buttons go full width on
 * mobile and revert to content width on desktop.
 */
export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 md:flex-row-reverse [&>*]:w-full md:[&>*]:w-auto">
      {children}
    </div>
  );
}

export const DialogTitle = forwardRef<
  HTMLHeadingElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("type-heading-sm text-text", className)}
      {...props}
    />
  );
});

export const DialogDescription = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("type-body-base text-text-muted", className)}
      {...props}
    />
  );
});
